import { CssBaseline } from '@mui/material'
import Box from '@mui/material/Box'
import { ThemeProvider } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import * as Sentry from '@sentry/react'
import themeKN from 'assets/theme/index'
import KNLoader from 'components/KNLoader/KNLoader'
import { GuestUserProvider } from 'context/authentication/GuestUserContext'
import { getSelectedCompany, getStartPage } from 'context/authentication/User.helpers'
import { UserContext } from 'context/authentication/UserContext'
import { MenuContextProvider } from 'context/menu/MenuContext'
import { TripDetailsProvider } from 'context/trips/TripDetailsContext'
import { TripsViewProvider } from 'context/TripsViewContext'
import { getActiveRoute } from 'global/helpers/activeRoute'
import { isPublicPath } from 'global/helpers/authorization'
import { getDateLocale } from 'global/helpers/dates'
import { isSentryEnabled } from 'global/helpers/environment'
import Support from 'modules/Support/Support'
import { type ReactElement, useContext, useEffect } from 'react'
import { Route, Switch, useHistory } from 'react-router-dom'
import Deca from 'screens/Deca/Deca'
import ErrorNotFound from 'screens/Error/ErrorNotFound'
import Home from 'screens/Home/Home'
import Login from 'screens/Login/Login'
import QuoteLink from 'screens/QuoteLink/QuoteLink'
import ResetPassword from 'screens/ResetPassword/ResetPassword'
import Weblink from 'screens/Weblink/Weblink'

export const MonitoredRoute = isSentryEnabled() ? Sentry.withSentryRouting(Route) : Route

const App = (): ReactElement => {
  const history = useHistory()
  const { pathname, hash } = history.location
  const { user, userLoading, loadUser } = useContext(UserContext)

  useEffect(() => {
    (async () => {
      await loadUser()
    })()
  }, [])

  // Standard redirects
  useEffect(() => {
    if (!user && !userLoading && !hash && !isPublicPath(pathname)) {
      const selectedCompanyCid = getSelectedCompany()?.cid
      history.push(selectedCompanyCid ? `/login?redirect=${pathname}&company=${selectedCompanyCid}` : '/login')
    }
    if (user && !userLoading && pathname === '/login') {
      history.push(getStartPage())
    }
    if (user && location.pathname !== '/') {
      const validRoute = !!getActiveRoute(location.pathname)
      if (!validRoute) {
        history.push('/not-found')
      }
    }
  }, [user, userLoading])

  const isDecaRoute = pathname.toLowerCase().startsWith('/deca')

  if (userLoading && pathname !== '/login' && !isDecaRoute) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <KNLoader />
      </Box>
    )
  }

  const externalRouteWL = (): ReactElement => {
    return (
      <MonitoredRoute exact path="/wl/:jwt">
        <TripsViewProvider>
          <TripDetailsProvider>
            <Weblink />
          </TripDetailsProvider>
        </TripsViewProvider>
        <Support />
      </MonitoredRoute>
    )
  }

  const externalRouteQL = (): ReactElement => {
    return (
      <MonitoredRoute exact path="/ql/:token">
        <GuestUserProvider>
          <QuoteLink />
        </GuestUserProvider>
      </MonitoredRoute>
    )
  }

  const externalRouteDeca = (): ReactElement => (
    <MonitoredRoute exact path="/deca/:token">
      <Deca />
    </MonitoredRoute>
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateLocale()}>
        <ThemeProvider theme={themeKN}>
          <MenuContextProvider>
          <CssBaseline />
          <Switch>
            {externalRouteWL()}
            {externalRouteQL()}
            {externalRouteDeca()}
            <MonitoredRoute path="/login">
              <Login />
            </MonitoredRoute>
            <MonitoredRoute path="/login/:action">
              <Login />
            </MonitoredRoute>
            <MonitoredRoute path="/reset">
              <ResetPassword />
            </MonitoredRoute>
            <Route path="/not-found">
              <ErrorNotFound />
            </Route>
            <MonitoredRoute path="/">
              <Home />
            </MonitoredRoute>
          </Switch>
          </MenuContextProvider>
        </ThemeProvider>
    </LocalizationProvider>
  )
}

export default App
